import React, { useEffect, useState } from "react";
import { Warning, ArrowClockwise, Info, X } from "@phosphor-icons/react";
import { api } from "@/lib/api";

// Chalk verdict logic — teaches users how elite DFS players read ownership.
// Blanket "cap chalk at 15%" is a rookie heuristic. Pros distinguish
// "good chalk" (high-owned AND salary-efficient — embrace it) from
// "bad chalk" (high-owned WITHOUT the value support — fade it).
//
// Thresholds tuned for real-season slates (25%+ = chalk territory). In the
// offseason preview pool ownership is uniformly ~3%, so most rows will
// classify as UNDER_OWNED (informational, not a real-slate verdict).
const OWN_CHALK = 25;
const OWN_UNDER = 15;
const VAL_GOOD = 3.0;
const VAL_BAD = 2.5;

const classifyChalk = (row) => {
  const own = Number(row?.own ?? 0);
  const val = Number(row?.value_ratio ?? 0);
  if (own < OWN_UNDER) return { label: "LOW OWN", tone: "neutral", note: "Not chalk — ownership too low to worry about duplication." };
  if (own >= OWN_CHALK && val >= VAL_GOOD) return { label: "GOOD CHALK", tone: "good", note: "High-owned AND salary-efficient. Elite pros eat this and differentiate elsewhere (kicker / defense / flex)." };
  if (own >= OWN_CHALK && val < VAL_BAD) return { label: "BAD CHALK", tone: "bad", note: "High-owned WITHOUT value support. Prime pivot target — cap exposure or fade for GPPs." };
  if (own >= OWN_CHALK) return { label: "BORDERLINE", tone: "meh", note: "High-owned at moderate value. In GPPs, elite pros often cap slightly below projected ownership (e.g., 35% projected → 25% cap) to gain leverage." };
  return { label: "MODERATE", tone: "neutral", note: "Below chalk threshold — situational play. Own% × Value drives the alert score." };
};

const toneClasses = {
  good: "text-[#39FF14] border-[#39FF14]/40 bg-[#39FF14]/5",
  bad: "text-[#ef4444] border-[#ef4444]/40 bg-[#ef4444]/5",
  meh: "text-[#facc15] border-[#facc15]/40 bg-[#facc15]/5",
  neutral: "text-zinc-500 border-zinc-800 bg-zinc-950/40",
};

// Educational "?" popover — teaches good-chalk-vs-bad-chalk framing directly
// in the UI, using the current row #1 as a live illustration. Zero new
// plumbing — just interprets the signals already displayed.
function ChalkGlossaryPopover({ topRow, onClose }) {
  useEffect(() => {
    const h = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const example = topRow ? classifyChalk(topRow) : null;

  return (
    <div
      data-testid="chalk-glossary-popover"
      className="absolute z-50 top-6 left-0 w-[340px] max-w-[calc(100vw-32px)] bg-[#0a0a0a] border-2 border-[#FF8A00] p-4 shadow-[0_0_28px_-8px_#FF8A00]"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <div className="mono text-[9px] uppercase tracking-[0.28em] text-[#FF8A00] font-bold">
            Reading Chalk Correctly
          </div>
          <div className="text-sm text-white font-black mt-0.5">Good chalk vs. bad chalk</div>
        </div>
        <button
          onClick={onClose}
          data-testid="chalk-glossary-close"
          className="text-zinc-500 hover:text-white transition-colors flex-shrink-0"
          aria-label="Close"
        >
          <X size={16} weight="bold" />
        </button>
      </div>
      <p className="text-[11px] text-zinc-400 leading-relaxed mb-3">
        Blanket "cap all chalk at 15%" is a rookie rule. Pros look at
        <span className="text-white font-semibold"> ownership × value </span>
        together and decide per-player.
      </p>
      <div className="space-y-1.5 mb-3">
        <div className="flex items-start gap-2 text-[11px]">
          <span className="mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 bg-[#39FF14]/10 text-[#39FF14] border border-[#39FF14]/40 shrink-0 mt-0.5">
            Good
          </span>
          <span className="text-zinc-300 leading-snug">
            Own ≥ 25% <span className="text-zinc-500">AND</span> VAL ≥ 3.0 → eat it. Differentiate at kicker / DST / flex instead.
          </span>
        </div>
        <div className="flex items-start gap-2 text-[11px]">
          <span className="mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 bg-[#facc15]/10 text-[#facc15] border border-[#facc15]/40 shrink-0 mt-0.5">
            Meh
          </span>
          <span className="text-zinc-300 leading-snug">
            Own ≥ 25% <span className="text-zinc-500">AND</span> VAL 2.5-3.0 → cap slightly below projected own (35% → 25% cap) for leverage.
          </span>
        </div>
        <div className="flex items-start gap-2 text-[11px]">
          <span className="mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 bg-[#ef4444]/10 text-[#ef4444] border border-[#ef4444]/40 shrink-0 mt-0.5">
            Bad
          </span>
          <span className="text-zinc-300 leading-snug">
            Own ≥ 25% <span className="text-zinc-500">AND</span> VAL &lt; 2.5 → prime pivot target. Use the red-flag icon in the pool to swap.
          </span>
        </div>
      </div>
      {example && (
        <div className="border-t border-zinc-800 pt-2.5 mt-2.5">
          <div className="mono text-[9px] uppercase tracking-widest text-zinc-500 mb-1">
            Live example · this pool's #1 chalk
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[12px] text-white font-semibold">{topRow.name}</span>
            <span className="mono text-[9px] text-zinc-500">{topRow.pos} · {Number(topRow.own).toFixed(1)}% own · {Number(topRow.value_ratio).toFixed(2)}x</span>
          </div>
          <div className={`mt-1.5 text-[11px] leading-snug px-2 py-1.5 border ${toneClasses[example.tone]}`}>
            <span className="mono text-[9px] uppercase tracking-widest font-bold">{example.label}</span>
            <span className="text-zinc-300 ml-2">{example.note}</span>
          </div>
        </div>
      )}
      <div className="mt-3 text-[10px] text-zinc-600 leading-relaxed">
        Cash games are the exception — pros routinely load 40-50%+ chalk when it's mathematically best. Fading chalk in cash <span className="text-zinc-400">lowers</span> win rate.
      </div>
    </div>
  );
}

// Chalk Watch — 5-row scorecard color-graded by chalk-alert intensity.
// Shown above the AI Consensus Advisor in WarPaint-Native mode so users
// can eyeball which value plays are tipping into duplication territory
// without waiting on a Claude call. Uses the same math as the synthetic
// advisor payload (`chalk_alert = value_ratio × (own + 1)`).
export default function ChalkWatchWidget({ autoLoad = true, limit = 5, variant = "full" }) {
  const [rows, setRows] = useState([]);
  const [maxAlert, setMaxAlert] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [glossaryOpen, setGlossaryOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.aiChalkWatch(limit);
      setRows(r.rows || []);
      setMaxAlert(r.max_alert || 0);
      setLoaded(true);
    } catch (e) {
      // Silent — the widget only shows when we actually have rows.
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (autoLoad) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit]);

  // Refresh when the auto-advisor CustomEvent fires (a new pool likely
  // means new chalk-alert scores worth resurfacing).
  useEffect(() => {
    const onAuto = () => load();
    // Also refresh when the DK pool ingests fresh data — the pool refresh
    // events already dispatched by DataPipeline let us stay current in the
    // Optimizer without polling.
    const onPool = () => load();
    window.addEventListener("wp:consensus-advisor-auto", onAuto);
    window.addEventListener("wp:pool-refreshed", onPool);
    return () => {
      window.removeEventListener("wp:consensus-advisor-auto", onAuto);
      window.removeEventListener("wp:pool-refreshed", onPool);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!loaded && !loading) return null;
  if (loaded && rows.length === 0) return null; // nothing to show — stay quiet

  if (variant === "slim") {
    return (
      <SlimStrip
        rows={rows}
        maxAlert={maxAlert}
        loading={loading}
        onRefresh={load}
        glossaryOpen={glossaryOpen}
        setGlossaryOpen={setGlossaryOpen}
      />
    );
  }

  return (
    <section
      data-testid="chalk-watch-widget"
      className="bg-[#121212] border border-zinc-800 p-4"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 relative">
          <Warning size={14} className="text-[#FF8A00]" weight="fill" />
          <div className="overline text-[10px] tracking-widest text-zinc-300">
            Chalk Watch
          </div>
          <span className="mono text-[9px] uppercase tracking-widest text-zinc-500 border border-zinc-800 px-1.5 py-0.5">
            Value × Own
          </span>
          <button
            type="button"
            data-testid="chalk-glossary-toggle"
            onClick={() => setGlossaryOpen((v) => !v)}
            title="How to read chalk — good vs. bad"
            aria-label="Learn how to interpret chalk"
            className="text-zinc-500 hover:text-[#FF8A00] transition-colors"
          >
            <Info size={13} weight={glossaryOpen ? "fill" : "regular"} />
          </button>
          {glossaryOpen && (
            <ChalkGlossaryPopover topRow={rows[0]} onClose={() => setGlossaryOpen(false)} />
          )}
        </div>
        <button
          data-testid="chalk-watch-refresh"
          onClick={load}
          disabled={loading}
          className="flex items-center gap-1 text-[10px] mono uppercase tracking-widest text-zinc-500 hover:text-[#39FF14] disabled:opacity-40 transition-colors"
          title="Recompute from current DK pool"
        >
          <ArrowClockwise size={12} className={loading ? "animate-spin" : ""} />
          {loading ? "…" : "Refresh"}
        </button>
      </div>
      <div className="overline text-[9px] text-zinc-500 mb-3">
        Highest value-ratio × projected-ownership plays — red rows are already tipping toward
        lineup-duplication territory.
      </div>
      <div className="space-y-1.5" data-testid="chalk-watch-rows">
        {rows.map((row, i) => (
          <ChalkWatchRow key={`${row.name}-${i}`} row={row} maxAlert={maxAlert} rank={i + 1} />
        ))}
      </div>
    </section>
  );
}

// Slim horizontal strip variant — pinned above the Optimizer's Player Pool
// controls so the "which of my locks is already overexposed" read is one
// glance away while building lineups.
function SlimStrip({ rows, maxAlert, loading, onRefresh, glossaryOpen, setGlossaryOpen }) {
  return (
    <div
      data-testid="chalk-watch-slim"
      className="flex items-center gap-2 px-4 py-2 border-b border-zinc-900 bg-black/40 flex-wrap"
    >
      <div className="flex items-center gap-1.5 shrink-0 relative">
        <Warning size={11} className="text-[#FF8A00]" weight="fill" />
        <span className="mono text-[9px] uppercase tracking-widest text-zinc-400">
          Chalk Watch
        </span>
        <button
          type="button"
          data-testid="chalk-glossary-toggle-slim"
          onClick={() => setGlossaryOpen((v) => !v)}
          title="How to read chalk — good vs. bad"
          aria-label="Learn how to interpret chalk"
          className="text-zinc-500 hover:text-[#FF8A00] transition-colors"
        >
          <Info size={11} weight={glossaryOpen ? "fill" : "regular"} />
        </button>
        {glossaryOpen && (
          <ChalkGlossaryPopover topRow={rows[0]} onClose={() => setGlossaryOpen(false)} />
        )}
      </div>
      <div className="flex items-center gap-1.5 flex-wrap flex-1" data-testid="chalk-watch-slim-rows">
        {rows.map((row, i) => {
          const alert = Number(row?.chalk_alert ?? 0);
          const own = Number(row?.own ?? 0);
          const proj = Number(row?.proj ?? 0);
          const vr = Number(row?.value_ratio ?? 0);
          const pct = maxAlert > 0 ? Math.max(0.05, alert / maxAlert) : 0;
          const { ring, border } = alertColors(pct);
          return (
            <div
              key={`slim-${row?.name}-${i}`}
              data-testid={`chalk-watch-slim-row-${i + 1}`}
              title={`${row?.name || "?"} · ${row?.pos || "?"} ${row?.team || "?"} · ${proj.toFixed(1)} proj · value ratio ${vr.toFixed(2)}`}
              className={`flex items-center gap-2 px-2 py-0.5 border ${border} bg-zinc-950/60`}
            >
              <span className="mono text-[9px] text-zinc-500">#{i + 1}</span>
              <span className="text-[11px] text-white font-medium truncate max-w-[130px]">
                {row?.name || "—"}
              </span>
              <span className="mono text-[9px] text-zinc-500">{row?.pos || "—"}</span>
              <span className="mono text-[9px] text-zinc-400">{own.toFixed(1)}%</span>
              <span className={`mono text-[10px] font-bold ${ring}`}>{alert.toFixed(1)}</span>
            </div>
          );
        })}
      </div>
      <button
        onClick={onRefresh}
        disabled={loading}
        title="Recompute Chalk Watch from current pool"
        data-testid="chalk-watch-slim-refresh"
        className="shrink-0 flex items-center gap-1 text-[9px] mono uppercase tracking-widest text-zinc-500 hover:text-[#39FF14] disabled:opacity-40 transition-colors"
      >
        <ArrowClockwise size={10} className={loading ? "animate-spin" : ""} />
        {loading ? "…" : "Refresh"}
      </button>
    </div>
  );
}

function ChalkWatchRow({ row, maxAlert, rank }) {
  const pct = maxAlert > 0 ? Math.max(0.05, row.chalk_alert / maxAlert) : 0;
  const { bar, ring } = alertColors(pct);
  const verdict = classifyChalk(row);
  return (
    <div
      data-testid={`chalk-watch-row-${rank}`}
      className="relative flex items-center gap-3 border border-zinc-900 bg-black/40 px-3 py-2 overflow-hidden"
    >
      {/* Colored intensity bar behind the row */}
      <div
        className="absolute inset-y-0 left-0 opacity-25"
        style={{ width: `${pct * 100}%`, background: bar }}
        aria-hidden
      />
      <span className={`relative mono text-[10px] font-bold ${ring} w-4 shrink-0`}>#{rank}</span>
      <div className="relative flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-white truncate">{row.name}</span>
          <span className="mono text-[9px] uppercase tracking-widest text-zinc-500">
            {row.pos} · {row.team}
          </span>
          <span
            data-testid={`chalk-verdict-${rank}`}
            title={verdict.note}
            className={`mono text-[8px] uppercase tracking-widest font-bold px-1.5 py-0.5 border ${toneClasses[verdict.tone]}`}
          >
            {verdict.label}
          </span>
        </div>
      </div>
      <div className="relative flex items-center gap-4 mono text-[10px] shrink-0">
        <div className="flex flex-col items-end w-14">
          <span className="text-zinc-500 uppercase text-[8px] tracking-widest">Proj</span>
          <span className="text-white">{row.proj.toFixed(1)}</span>
        </div>
        <div className="flex flex-col items-end w-14">
          <span className="text-zinc-500 uppercase text-[8px] tracking-widest">Own</span>
          <span className="text-white">{row.own.toFixed(1)}%</span>
        </div>
        <div className="flex flex-col items-end w-14">
          <span className="text-zinc-500 uppercase text-[8px] tracking-widest">Value</span>
          <span className="text-white">{row.value_ratio.toFixed(2)}</span>
        </div>
        <div className="flex flex-col items-end w-16">
          <span className="text-zinc-500 uppercase text-[8px] tracking-widest">Alert</span>
          <span className={`font-bold ${ring}`}>{row.chalk_alert.toFixed(1)}</span>
        </div>
      </div>
    </div>
  );
}

// Traffic-light gradient: green (safe pivot) → yellow → orange → red (heavy duplication).
function alertColors(pct) {
  if (pct >= 0.85) return { bar: "#ef4444", ring: "text-[#ef4444]", border: "border-[#ef4444]/40" };
  if (pct >= 0.65) return { bar: "#FF8A00", ring: "text-[#FF8A00]", border: "border-[#FF8A00]/40" };
  if (pct >= 0.45) return { bar: "#facc15", ring: "text-[#facc15]", border: "border-[#facc15]/40" };
  return { bar: "#39FF14", ring: "text-[#39FF14]", border: "border-[#39FF14]/40" };
}
